import React, { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useLocation } from 'react-router';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { BUTTON_TYPE, SIZE, VARIANT } from '@motorway/mw-highway-code/enums';
import { InputVRM } from '@motorway/mw-highway-code/pre-alpha';
import { Intent } from '@motorway/mw-highway-code/src/types';

import cypressIds from 'CypressId';

import { type HeroAnimationState } from 'Utilities/animation';
import { vehicleType } from 'Utilities/helpers';
import { applyCypressData } from 'Utilities/index';

import { FeedbackTooltip } from 'Storybook/components/local/FeedbackTooltip/FeedbackTooltip';

import useHeroVrmInput from '../hooks/useHeroVrmInput';

import LocalTexts from './VrmInputTexts.json';

import styles from './VrmInputVRM.scss';

const LocalT = new MDText(LocalTexts);

export type VrmInputVRMProps = {
  analyticsEvents?: {
    onButtonClick?: (vrm: string) => void;
    onInputFocus?: () => void;
    onLandingPageInputFocus?: () => void;
  };
  loadingHandler?: (val: HeroAnimationState) => void;
  onError?: (...args: unknown[]) => void;
  onSuccess?: () => void;
  required?: boolean;
  setShowMileage?: (value: boolean) => void;
  valuationHub?: boolean;
  vrm?: string;
};

// TODO - Move these over to RFF such as '../mileageInput/MileageInput.jsx'
const VrmInputVRM = ({
  analyticsEvents = {},
  loadingHandler = () => { },
  onError = () => {},
  onSuccess = () => { },
  required = true,
  setShowMileage = () => { },
  valuationHub = false,
  vrm = '',
}: VrmInputVRMProps) => {
  const [html5validation, setHtml5validation] = useState(true);

  const location = useLocation();

  const {
    getErrorMsg,
    hasApiError: isApiError,
    loading,
    onFormSubmit,
    onInputBlur,
    onInputChange,
    onInputFocus,
    showIntent,
    vrmValue,
    wrongVRM,
  } = useHeroVrmInput({
    loadingHandler,
    onError,
    onSuccess,
    setShowMileage,
    valuationHub,
    vrm,
  });

  const placeholder = LocalT.translate('vrmChecker.placeholder');
  const ctaNonErrorLabel = LocalT.translate(valuationHub
    ? 'cta.startTracking'
    : 'cta.valueCar', { vehicle: vehicleType(location.pathname) });

  const ctaLabel = (isApiError || wrongVRM)
    ? LocalT.translate('cta.again')
    : ctaNonErrorLabel;

  const commonInputAttributes = {
    ...applyCypressData(cypressIds.fields.vrmInputField),
    onBlur: onInputBlur,
    onChange: ({ currentTarget: { value } }: { currentTarget: HTMLInputElement }) => onInputChange(value),
    onFocus: () => {
      analyticsEvents?.onInputFocus?.();
      analyticsEvents?.onLandingPageInputFocus?.();
      onInputFocus();
    },
    placeholder,
  };

  useEffect(() => {
    setHtml5validation(false);
  }, []);

  return (
    <Form
      onSubmit={() => onFormSubmit()}
      render={({ handleSubmit }) => (
        <form
          className={styles.component}
          data-cy="vrmInputForm"
          noValidate={!html5validation}
          onSubmit={handleSubmit}
        >
          <Field
            id='vrm-input'
            name='vrm-input'
          >
            {({ id }) => (
              <FeedbackTooltip
                appendTo="parent"
                className={styles.tooltip}
                content={LocalT.translate(`vrmChecker.${getErrorMsg()}`)}
                intent={showIntent()}
                interactive={false}
                placement='top'
                zIndex={10}
              >
                <div className={styles.input}>
                  <InputVRM
                    fullWidth
                    data-cy="invalidVrm"
                    formProps={{
                      input: {
                        ...commonInputAttributes,
                        'aria-label': 'vrm-input',
                        autoCapitalize: 'characters',
                        autoComplete: 'off',
                        maxLength: 10,
                        name: id,
                        readOnly: loading,
                        required,
                        type: 'text',
                        value: vrmValue,
                      },
                    }}
                    id={id}
                    intent={showIntent() as Intent}
                    label={''}
                    showLabel={false}
                    size="large"
                  />
                </div>
              </FeedbackTooltip>
            )}
          </Field>
          <Button
            data-thc-button
            fullWidth
            icon="chevron"
            label= {ctaLabel}
            loading={loading}
            onClick={() => analyticsEvents?.onButtonClick?.(vrm)}
            size={ SIZE.LARGE }
            type={BUTTON_TYPE.SUBMIT}
            variant={VARIANT.PRIMARY}
            {...applyCypressData(cypressIds.buttons.valueCarButton)}
          />
        </form>
      )}
    />
  );
};

export default VrmInputVRM;
